import { Deferred } from './utils';
import { EventListener } from './eventListener';
import {
  Chain,
  SignatureMethod,
  Wallet,
  WalletSignedProof,
  EventTypeResponse,
  EventTypeRequest,
  EventTypeRequestWithPayload,
  ErrorResponseData,
} from '../types';
import logger from './logger';
import { v4 as uuid } from 'uuid';
import { ResponseEvent } from './remoteSign';
const DEFAULT_TIMEOUT = 1000;

type Powo = {
  chain: Chain;
  proof: string;
  signatureMethod: SignatureMethod;
};

type Request = {
  promise:
    | Deferred<string>
    | Deferred<WalletSignedProof>
    | Deferred<Uint8Array>
    | Deferred<Powo>;
  timeout: NodeJS.Timeout;
  requestId: string;
  responseType: EventTypeResponse;
  requestType: EventTypeRequest;
};

export function RemoteWallet({
  eventListener,
  chain,
  timeout = DEFAULT_TIMEOUT,
  instanceId,
  debug = false,
}: {
  eventListener: EventListener;
  chain: Chain;
  timeout?: number;
  instanceId: string;
  debug?: boolean;
}): Wallet {
  const requests: Request[] = [];

  /**
   * try to find the matching request to the incoming response, use these methods in order or precedence:
   * - requestId
   * - requestType (for error responses)
   * - responseType
   * @param {ResponseEvent} response
   * @returns {Request | undefined}
   */
  const getRequestFromResponse = (
    response: ResponseEvent
  ): Request | undefined => {
    const requestFromId = requests.find(
      r => r.requestId === response.requestId
    );
    if (requestFromId) {
      return requestFromId;
    }
    if (response.event === EventTypeResponse.RESPONSE_ERROR) {
      return requests.find(
        r => r.requestType === (response.data as ErrorResponseData).request
      );
    }
    // fallback to using the request type if legacy responses don't have a requestId
    return requests.find(r => r.responseType === response.event);
  };

  const removeRequest = (request: Request) => {
    // remove the request from the list of pending requests
    requests.splice(requests.indexOf(request), 1);
  };
  const logDebug = (message: string, obj: unknown) =>
    debug && logger.debug(`[RemoteWallet] ${message}`, obj);

  const emit = (event: EventTypeRequest) => {
    logger.debug('emitting postMessage event from civic sign', event);
    eventListener.postMessage(event, '*');
  };

  const emitWithPayload = (event: EventTypeRequestWithPayload) => {
    logger.debug('emitting postMessageWithPayload event from civic sign', {
      ...event,
      instanceId,
    });
    eventListener.postMessageWithPayload({ ...event, instanceId }, '*');
  };

  function waitForResponse<T>(r: EventTypeRequest, p: Deferred<T>) {
    return setTimeout(() => {
      p.reject(new Error(`No response from remote app for ${r}`));
    }, timeout);
  }

  const supportedEvents: EventTypeResponse[] = [
    EventTypeResponse.RESPONSE_PUBLIC_KEY,
    EventTypeResponse.RESPONSE_DID,
    EventTypeResponse.RESPONSE_SIGNED_PROOF,
    EventTypeResponse.RESPONSE_SIGNED_MESSAGE,
    EventTypeResponse.RESPONSE_ERROR,
  ];

  eventListener.addEventListener(response => {
    if (!supportedEvents.includes(response.event)) {
      return;
    }
    logDebug('Received event from remote app', { instanceId, response });
    if (response.instanceId && response.instanceId !== instanceId) {
      logDebug(
        'Event from remote app with different instanceId, not responding',
        { instanceId, response }
      );
      return;
    }
    const request = getRequestFromResponse(response);
    if (!request) {
      logDebug('No request found for response, not responding', {
        instanceId,
        response,
      });
      return;
    }
    switch (response.event) {
      case EventTypeResponse.RESPONSE_ERROR:
        logDebug(
          'Received RESPONSE_ERROR from remote app, rejecting the appropriate promise',
          response.data
        );
        request.promise.reject(response.data.error);
        clearTimeout(request.timeout);
        break;
      case EventTypeResponse.RESPONSE_SIGNED_MESSAGE:
      case EventTypeResponse.RESPONSE_PUBLIC_KEY:
      case EventTypeResponse.RESPONSE_DID:
        clearTimeout(request.timeout);
        request.promise.resolve(response.data);
        // remove the request from the list of pending requests
        removeRequest(request);
        break;
      case EventTypeResponse.RESPONSE_SIGNED_PROOF:
        clearTimeout(request.timeout);
        // need to handle the legacy case where events are being emitted by an older version of civic-sign
        // with no signatureMethod
        if (typeof response.data === 'string') {
          (request.promise as Deferred<Powo>).resolve({
            chain,
            proof: response.data,
            signatureMethod: SignatureMethod.TRANSACTION,
          });
          removeRequest(request);
          break;
        }
        (request.promise as Deferred<Powo>).resolve({
          chain,
          signatureMethod: response.data.signatureMethod,
          proof: response.data.proof,
        });
        removeRequest(request);
        break;
    }
  });

  return {
    getPublicKey: () => {
      const getPublicKeyPromise = new Deferred<string>();
      const request = EventTypeRequest.REQUEST_PUBLIC_KEY;
      const waitForPublicKey = waitForResponse(request, getPublicKeyPromise);
      emit(request); // for legacy listeners
      const requestId = uuid();
      emitWithPayload({
        request,
        requestId,
      });
      requests.push({
        promise: getPublicKeyPromise,
        timeout: waitForPublicKey,
        requestId,
        requestType: request,
        responseType: EventTypeResponse.RESPONSE_PUBLIC_KEY,
      });
      return getPublicKeyPromise.promise;
    },
    getDid: () => {
      const getDidPromise = new Deferred<string>();
      const request = EventTypeRequest.REQUEST_DID;
      const waitForDid = waitForResponse(request, getDidPromise);
      emit(request); // for legacy listeners
      const requestId = uuid();
      emitWithPayload({
        request: request,
        requestId,
      });
      requests.push({
        promise: getDidPromise,
        timeout: waitForDid,
        requestId,
        requestType: request,
        responseType: EventTypeResponse.RESPONSE_DID,
      });
      return getDidPromise.promise;
    },
    signProof: (message: string) => {
      const signProofPromise = new Deferred<WalletSignedProof>();
      const request = EventTypeRequest.REQUEST_SIGNED_PROOF;
      const waitForSignedProof = waitForResponse(
        EventTypeRequest.REQUEST_SIGNED_PROOF,
        signProofPromise
      );
      emit(request); // for legacy listeners
      const requestId = uuid();
      emitWithPayload({
        request,
        payload: message,
        requestId,
      });
      requests.push({
        promise: signProofPromise,
        timeout: waitForSignedProof,
        requestId,
        requestType: request,
        responseType: EventTypeResponse.RESPONSE_SIGNED_PROOF,
      });
      return signProofPromise.promise;
    },
    signMessage: (message: Uint8Array) => {
      const signMessagePromise = new Deferred<Uint8Array>();
      const request = EventTypeRequest.REQUEST_SIGNED_MESSAGE;
      const waitForSignedMessage = waitForResponse(request, signMessagePromise);
      const requestId = uuid();
      emitWithPayload({
        request,
        payload: message,
        requestId,
      });
      requests.push({
        promise: signMessagePromise,
        timeout: waitForSignedMessage,
        requestId,
        requestType: request,
        responseType: EventTypeResponse.RESPONSE_SIGNED_MESSAGE,
      });
      return signMessagePromise.promise;
    },
    chain,
  };
}
